import type { BasilHttp } from '../write/writeTypes';

/**
 * Basil v0.2, P3 — the two reads that turn an attachment into a word.
 *
 * The episode read already says WHAT is attached: a step carries `stepAudios[]` and a `video`, and
 * a scene's location carries a `backgroundId` and an `audioId`. What it does not say is what any of
 * those is CALLED. These two reads supply the titles, and nothing else:
 *
 *  * `GET /v1/audio/book/:bookId` -> `{ audios: [{ id, title, type }] }`, read once per open;
 *  * `GET /v1/backgrounds/:id` -> the background row, for its `label`, one per distinct background.
 *
 * FAIL-OPEN, with one difference from the v0.1.1 name reads that matters: a read that failed is
 * remembered as HAVING FAILED. A missing title and an unreadable list are different facts about the
 * writer's episode, and the margin says which one it is — "Sound attached" when the attachment is
 * known and the title is not, "Sound details unavailable" when the lookup could not answer. A
 * failed lookup is never shown as absence.
 *
 * Nothing here plays, fetches or renders an asset: no url leaves these functions, and neither read
 * touches the write path.
 */

/** What one list read produced: whether it answered, and the titles it carried. */
export interface TitleRead {
  /** true only when the read answered at all. A read of many ids answers even if some ids did not. */
  ok: boolean;
  /** `id -> title`, for the rows that carried one. */
  titles: Record<string, string>;
  /** `id -> 'music' | 'effect' | …`, for the rows that carried a type. */
  kinds: Record<string, string>;
  /**
   * T17-07: the ids whose OWN lookup could not answer.
   *
   * The background labels are one request per background. One of them failing says nothing about
   * the others, and marking the whole read unavailable told a writer that a title the surface was
   * holding in its hand could not be read. Availability is per background, so it is recorded per
   * background; an id absent from here and from `titles` simply has no label of its own.
   */
  missing?: Record<string, true>;
}

/** Nothing read: the value a skipped, failed or empty read produces. */
export const NO_TITLES: TitleRead = Object.freeze({ ok: false, titles: {}, kinds: {} }) as TitleRead;

export function bookAudioPath(bookId: number | string): string {
  return `/v1/audio/book/${bookId}`;
}

export function backgroundPath(backgroundId: number | string): string {
  return `/v1/backgrounds/${backgroundId}`;
}

type AudioRow = { id?: unknown; title?: unknown; type?: unknown };

/**
 * The book's sounds and music, by id.
 *
 * One read per open. The dev book carries a few thousand rows and the engine serves them in one
 * body; the surface keeps only the id, the title and the kind, and drops everything else on the
 * floor rather than holding a url it has no use for.
 */
export async function readBookAudio(http: BasilHttp, bookId: number): Promise<TitleRead> {
  const book = Number(bookId);

  if (!Number.isFinite(book) || book <= 0) {
    return NO_TITLES;
  }

  try {
    const response = await http.get<{ audios?: unknown }>(bookAudioPath(book));

    if (response && response.status === 503) {
      return NO_TITLES;
    }
    const rows = response && response.data ? response.data.audios : null;

    if (!Array.isArray(rows)) {
      return NO_TITLES;
    }
    const titles: Record<string, string> = {};
    const kinds: Record<string, string> = {};

    (rows as AudioRow[]).forEach((row) => {
      if (!row || row.id == null) {
        return;
      }
      if (typeof row.title === 'string' && row.title !== '') {
        titles[String(row.id)] = row.title;
      }
      if (typeof row.type === 'string' && row.type !== '') {
        kinds[String(row.id)] = row.type;
      }
    });

    return { ok: true, titles, kinds };
  } catch {
    return NO_TITLES;
  }
}

/** The background row's own word for itself, across the two shapes the engine answers with. */
function labelOf(data: unknown): string | null {
  const body = (data ?? {}) as Record<string, unknown>;
  const image = (body.image ?? body) as Record<string, unknown>;
  const label = image.label ?? image.alias;

  return typeof label === 'string' && label !== '' ? label : null;
}

/**
 * The labels of a set of backgrounds, by id. One request per distinct id, all in flight together.
 *
 * T17-07: availability is tracked PER BACKGROUND. One request failing is a fact about that one
 * background and about no other, so the read as a whole still answers (`ok: true`) and the ids that
 * could not be read are named in `missing`. Before this, a single failed request marked every
 * background unavailable, including the ones whose titles were already in hand.
 */
export async function readBackgroundLabels(
  http: BasilHttp,
  backgroundIds: readonly (string | number)[],
): Promise<TitleRead> {
  const wanted = Array.from(new Set(backgroundIds.map((id) => String(id)).filter((id) => id !== '' && id !== 'null')));

  if (wanted.length === 0) {
    return { ok: true, titles: {}, kinds: {} };
  }

  const answered = await Promise.all(wanted.map(async (id) => {
    try {
      const response = await http.get<unknown>(backgroundPath(id));

      if (response && response.status === 503) {
        return { id, ok: false, label: null as string | null };
      }

      return { id, ok: true, label: labelOf(response ? response.data : null) };
    } catch {
      return { id, ok: false, label: null as string | null };
    }
  }));
  const titles: Record<string, string> = {};
  const missing: Record<string, true> = {};

  answered.forEach((row) => {
    if (!row.ok) {
      missing[row.id] = true;

      return;
    }
    if (row.label) {
      titles[row.id] = row.label;
    }
  });

  return {
    ok: true, titles, kinds: {}, missing,
  };
}
